/**
 * Wishlist Products Component
 * Loads and displays wishlist products using Rivo API
 * and renders them using the product-card snippet
 */

/**
 * Get the locale-aware search URL
 * Extracts locale from current path if window.theme is not available
 */
function getSearchUrl() {
  // Prefer window.theme if available
  if (window.theme?.routes?.search_url) {
    return window.theme.routes.search_url;
  }

  // Extract locale from current path (e.g., /de-de/products/... -> /de-de or /de/products/... -> /de)
  const pathname = window.location.pathname;
  const localeMatch = pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/);

  if (localeMatch) {
    return `/${localeMatch[1]}/search`;
  }

  return '/search';
}

class WishlistProducts extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.products = [];
  }

  connectedCallback() {
    this.limit = parseInt(this.getAttribute('data-limit')) || 4;
    this.displayMode = this.getAttribute('data-display-mode') || 'grid';
    this.sectionId = this.getAttribute('data-section-id');

    // Set up Intersection Observer as fallback
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.loading && !this.loaded) {
            this._initLoadProducts();
            this.observer.disconnect();
          }
        });
      }, {
        rootMargin: '400px'
      });

      this.observer.observe(this);
    }

    // Preemptive loading during idle time
    this.schedulePreemptiveLoad();
  }

  schedulePreemptiveLoad() {
    if (document.readyState === 'complete') {
      this.queuePreemptiveLoad();
    } else {
      window.addEventListener('load', () => this.queuePreemptiveLoad(), { once: true });
    }
  }

  queuePreemptiveLoad() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        if (!this.loading && !this.loaded) {
          setTimeout(() => {
            if (!this.loading && !this.loaded) {
              this._initLoadProducts();
            }
          }, 300); // Latest priority
        }
      }, { timeout: 4000 });
    } else {
      setTimeout(() => {
        if (!this.loading && !this.loaded) {
          this._initLoadProducts();
        }
      }, 2500);
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Check if user is logged in
   * @returns {boolean}
   */
  isLoggedIn() {
    // Theme is defined as a const in scripts.liquid, not window.Theme
    if (typeof Theme !== 'undefined' && Theme.customer) {
      return Theme.customer.loggedIn === true;
    }
    return false;
  }

  /**
   * Initialize product loading (wait for RivoJS if needed)
   * Merges both Rivo and localStorage sources when logged in
   */
  _initLoadProducts() {
    // If user is not logged in, load from localStorage
    if (!this.isLoggedIn()) {
      this.loadProductsFromLocalStorage();
      return;
    }

    // User is logged in: merge both sources.
    // Wait briefly for RivoJS, but don't depend on it — with the Rivo app
    // removed the 'rivo-js-loaded' event never fires, and waiting forever
    // left this section as a permanent skeleton for logged-in users.
    // loadProductsMerged() already degrades to localStorage-only when
    // RivoJS is absent.
    if (typeof RivoJS !== 'undefined' && RivoJS.getFavoriteProducts) {
      this.loadProductsMerged();
    } else {
      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        this.loadProductsMerged();
      };
      setTimeout(start, 4000);
      document.addEventListener('rivo-js-loaded', start, { once: true });
    }
  }

  /**
   * Load wishlist products from localStorage (for non-logged-in users)
   */
  async loadProductsFromLocalStorage() {
    if (this.loading) {
      return;
    }

    this.loading = true;

    try {
      // Get liked products from localStorage
      const LIKED_PRODUCTS_STORAGE_KEY = 'theme:liked-products';
      const likedJson = localStorage.getItem(LIKED_PRODUCTS_STORAGE_KEY);

      if (!likedJson) {
        this.hideSection();
        return;
      }

      const likedHandles = JSON.parse(likedJson);

      if (!Array.isArray(likedHandles) || likedHandles.length === 0) {
        this.hideSection();
        return;
      }

      // Limit to the configured limit
      const handles = likedHandles.slice(0, this.limit);

      // Convert handles to product IDs
      const productIds = await this.convertHandlesToIds(handles);

      if (productIds.length === 0) {
        console.warn('[Wishlist] No valid product IDs found from localStorage');
        this.hideSection();
        return;
      }

      // Store product IDs for rendering
      this.productIds = productIds;
      await this.renderProducts();
      this.showSection();
    } catch (error) {
      console.error('[Wishlist] Error loading wishlist from localStorage:', error);
      this.hideSection();
    } finally {
      this.loading = false;
      this.loaded = true;
    }
  }

  /**
   * Convert product handles to product IDs
   * Fetches product data in parallel with throttling to avoid blocking
   * @param {string[]} handles - Array of product handles
   * @returns {Promise<string[]>} Array of product IDs
   */
  async convertHandlesToIds(handles) {
    if (!Array.isArray(handles) || handles.length === 0) {
      return [];
    }

    // Fetch products in parallel for better performance
    const fetchPromises = handles.map(async (handle) => {
      try {
        // Use Shopify product JSON endpoint
        const productUrl = `/products/${handle}.json`;
        const response = await fetch(productUrl);

        if (!response.ok) {
          console.warn(`[Wishlist] Product not found: ${handle}`);
          return null;
        }

        const data = await response.json();
        if (data.product && data.product.id) {
          return data.product.id.toString();
        }

        return null;
      } catch (error) {
        console.error(`[Wishlist] Error fetching product ${handle}:`, error);
        return null;
      }
    });

    // Wait for all fetches to complete
    const results = await Promise.all(fetchPromises);

    // Filter out nulls and return valid product IDs
    return results.filter(id => id !== null);
  }

  /**
   * Load wishlist products from Rivo API (for logged-in users)
   */
  async loadProducts() {

    if (this.loading) {
      return;
    }

    this.loading = true;

    try {
      // Check if RivoJS is available

      if (typeof RivoJS === 'undefined' || !RivoJS.getFavoriteProducts) {
        console.warn('[Wishlist] RivoJS is not available or getFavoriteProducts is missing');
        this.hideSection();
        return;
      }

      // Get favorite products from Rivo
      const resp = await RivoJS.getFavoriteProducts();

      // Check if the response is successful
      if (!resp || !resp.success || !resp.products || resp.products.length === 0) {
        this.hideSection();
        return;
      }


      // Extract product IDs from Rivo response
      const productIds = resp.products
        .map(p => p.remote_id)
        .filter(id => id)
        .slice(0, this.limit);


      if (productIds.length === 0) {
        console.warn('[Wishlist] No valid product IDs found');
        this.hideSection();
        return;
      }

      // Store product IDs for rendering
      this.productIds = productIds;
      await this.renderProducts();
      this.showSection();
    } catch (error) {
      console.error('[Wishlist] Error loading wishlist products:', error);
      console.error('[Wishlist] Error stack:', error.stack);
      this.hideSection();
    } finally {
      this.loading = false;
      this.loaded = true;
    }
  }

  /**
   * Load wishlist products from both Rivo and localStorage (for logged-in users)
   * Merges sources with deduplication
   */
  async loadProductsMerged() {
    if (this.loading) {
      return;
    }

    this.loading = true;

    try {
      // Get product IDs from both sources
      let rivoProductIds = [];
      let localProductIds = [];

      // Try to get from Rivo
      if (typeof RivoJS !== 'undefined' && RivoJS.getFavoriteProducts) {
        try {
          const resp = await RivoJS.getFavoriteProducts();
          if (resp && resp.success && resp.products && resp.products.length > 0) {
            rivoProductIds = resp.products
              .map(p => p.remote_id)
              .filter(id => id);
          }
        } catch (error) {
          console.warn('[Wishlist] Error fetching from Rivo, will use localStorage:', error);
        }
      }

      // Get from localStorage
      const LIKED_PRODUCTS_STORAGE_KEY = 'theme:liked-products';
      const likedJson = localStorage.getItem(LIKED_PRODUCTS_STORAGE_KEY);
      if (likedJson) {
        try {
          const likedHandles = JSON.parse(likedJson);
          if (Array.isArray(likedHandles) && likedHandles.length > 0) {
            // Convert handles to product IDs
            localProductIds = await this.convertHandlesToIds(likedHandles);
          }
        } catch (error) {
          console.warn('[Wishlist] Error reading from localStorage:', error);
        }
      }

      // Merge and deduplicate (Rivo takes precedence for ordering)
      const seenIds = new Set(rivoProductIds);
      const uniqueLocalIds = localProductIds.filter(id => {
        if (seenIds.has(id)) {
          return false;
        }
        seenIds.add(id);
        return true;
      });

      // Rivo products first, then localStorage-only products
      const allProductIds = [...rivoProductIds, ...uniqueLocalIds];

      console.log(`[Wishlist] Merged wishlists: ${rivoProductIds.length} from Rivo, ${uniqueLocalIds.length} from localStorage only`);

      if (allProductIds.length === 0) {
        console.log('[Wishlist] No products in merged wishlist');
        this.hideSection();
        return;
      }

      // Limit to configured limit
      this.productIds = allProductIds.slice(0, this.limit);
      await this.renderProducts();
      this.showSection();
    } catch (error) {
      console.error('[Wishlist] Error loading merged wishlist:', error);
      console.error('[Wishlist] Error stack:', error.stack);
      this.hideSection();
    } finally {
      this.loading = false;
      this.loaded = true;
    }
  }

  /**
   * Render products using Shopify search with section rendering
   * This ensures full SSR rendering including mock images and other custom features
   */
  async renderProducts() {

    try {
      // Build search query with product IDs from Rivo
      const searchQuery = this.productIds.map(id => `id:${id}`).join(' OR ');

      // Fetch rendered section using search endpoint
      const searchUrl = getSearchUrl();
      const url = `${searchUrl}?type=product&q=${encodeURIComponent(searchQuery)}&section_id=${encodeURIComponent(this.sectionId)}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch section HTML: ${response.status} ${response.statusText}`);
      }

      const html = await response.text();

      // Parse the response to extract the product grid
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the product grid in the rendered section
      const productGrid = doc.querySelector('.wishlist-products__grid');

      if (productGrid) {
        const targetContainer = this.querySelector('.wishlist-products__grid');
        if (targetContainer) {
          targetContainer.innerHTML = productGrid.innerHTML;
          // Wait for next frame to ensure DOM is rendered before reinitializing
          requestAnimationFrame(() => {
            this.reinitializeRivoButtons();
          });
        }
      } else {
        // Fallback: try to find resource-list
        const resourceList = doc.querySelector('.resource-list');
        if (resourceList) {
          const targetContainer = this.querySelector('.wishlist-products__grid');
          if (targetContainer) {
            targetContainer.innerHTML = resourceList.innerHTML;
            // Wait for next frame to ensure DOM is rendered before reinitializing
            requestAnimationFrame(() => {
              this.reinitializeRivoButtons();
            });
          }
        } else {
          // If neither found, use fallback rendering
          console.warn('[Wishlist] No product grid or resource-list found, using fallback');
          this.renderProductsFallback();
        }
      }
    } catch (error) {
      console.error('[Wishlist] Error rendering products via search:', error);
      console.error('[Wishlist] Error stack:', error.stack);
      // Fallback to basic rendering
      this.renderProductsFallback();
    }
  }

  /**
   * Fallback rendering method - shows a message since we don't have full product data
   */
  renderProductsFallback() {
    const container = this.querySelector('.wishlist-products__grid');
    if (!container) {
      console.warn('[Wishlist] No container found for fallback rendering');
      return;
    }

    // Without full product data, we can't render a meaningful fallback
    // Just show a simple message
    container.innerHTML = '<p>Unable to load wishlist products. Please refresh the page.</p>';
  }

  /**
   * Reinitialize Rivo favorite buttons after dynamically loading content
   */
  reinitializeRivoButtons() {
    try {
      if (window.RivoProfileAlpine) {
        const rivoAccountWidget = document.getElementById('rivo-account-slideout');
        if (rivoAccountWidget) {
          window.RivoProfileAlpine.$data(rivoAccountWidget).initializeFavoriteButtons();
        } else {
          console.warn('[Wishlist] Rivo account widget not found');
        }
      } else {
        console.warn('[Wishlist] RivoProfileAlpine not available');
      }
    } catch (error) {
      console.error('[Wishlist] Error reinitializing Rivo buttons:', error);
    }
  }

  /**
   * Hide the section
   */
  hideSection() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.style.display = 'none';
    } else {
      console.warn('[Wishlist] No parent section found to hide');
    }
  }

  /**
   * Show the section
   */
  showSection() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.style.display = '';
    } else {
      console.warn('[Wishlist] No parent section found to show');
    }
  }
}

// Register the custom element
if (!customElements.get('wishlist-products')) {
  customElements.define('wishlist-products', WishlistProducts);
} else {
}
